import {trackEvent} from '@hconnect/common/logging/Analytics'
import {dateFormatter, TableColumnType, SecondaryCell} from '@hconnect/uikit'
import {Button, TextSmallRegular} from '@hm/fundamental'
import Download from '@mui/icons-material/Download'
import React from 'react'
import {useTranslation} from 'react-i18next'
import {v4 as uuidV4} from 'uuid'

import {DocumentModel} from './Documents.types'

type Analytics = {
  userId?: string
  userMail?: string
  userCountry?: string
  userRoles?: any[]
  userIsInternal?: boolean
  customerId?: string
  customerName?: string
}

type DownloadDocumentProps = {
  document: DocumentModel
  analytics: Analytics
  downloadDocument: (jobId: string, documentId: string, fileName: string) => void
}
export const DownloadDocument = ({
  document,
  analytics,
  downloadDocument
}: DownloadDocumentProps) => {
  const {t} = useTranslation()
  const textDocument = t('sharePointDocuments.documentLabel')
  const documentId = document?.fileUrl
  const fileName = document.fileName

  return documentId ? (
    <div>
      <Button
        data-test-id="sharepoint-document-download"
        variant="tertiary"
        size="small"
        color="primary"
        style={{justifyContent: 'flex-start'}}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault()
          e.stopPropagation()
          const jobId: string = uuidV4()
          downloadDocument(jobId, documentId, fileName)
        }}
        icon={<Download />}
      >
        Download file
      </Button>
    </div>
  ) : (
    <div>{textDocument}</div>
  )
}
type DocumentColumnsProps = {
  downloadDocument: (jobId: string, documentId: string, fileName: string) => void
  analytics: Analytics
}

export const DocumentsColumns = ({downloadDocument, analytics}: DocumentColumnsProps) => {
  const {
    t,
    i18n: {language}
  } = useTranslation()


    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) {
            return `${bytes} B`
        }
        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`
        }
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

  const columnPool: {[key: string]: TableColumnType} = {
    documentName: {
      field: 'documentName',
      headerName: t('sharePointDocuments.documentName'),
      sortable: true,
      headerAlign: 'left',
      align: 'left',
        renderCell: (item: DocumentModel) =>
            <div className="flex justify-between gap-4">
                <TextSmallRegular className="flex-1">{item.fileName}</TextSmallRegular>
                <TextSmallRegular className="justify-self-end">{formatFileSize(item.fileSize)}</TextSmallRegular>
            </div>
    },

    date: {
      field: 'date',
      headerName: t('sharePointDocuments.date'),
      sortable: true,
      headerAlign: 'left',
      align: 'left',
      renderCell: (item: DocumentModel) => (
        <TextSmallRegular>{dateFormatter(item.createdAt, language)}</TextSmallRegular>
      )
    },
    project: {
      field: 'project',
      headerName: t('sharePointDocuments.project'),
      sortable: true,
      headerAlign: 'left',
      align: 'left',
      renderCell: (item: DocumentModel) => <TextSmallRegular>{item.project}</TextSmallRegular>
    },
    hubCategory: {
      field: 'hubCategory',
      headerName: t('sharePointDocuments.category'),
      sortable: true,
      headerAlign: 'left',
      align: 'left',
      renderCell: (item: DocumentModel) => <TextSmallRegular>{item.hubCategory}</TextSmallRegular>
    },
    download: {
      field: 'download',
      headerAlign: 'center',
      align: 'center',
      renderCell: (item) => (
        <DownloadDocument
          analytics={analytics}
          downloadDocument={downloadDocument}
          document={item}
        />
      )
    }
  }

  return [
    columnPool.documentName,
    columnPool.date,
    columnPool.hubCategory,
    columnPool.download
  ]
}
